import { useMemo, useState } from "react";
import { Select } from "risa-oasis-ui_v2";
import { BACKGROUND_COLORS_FOR_NOTES } from "../../../../../../shared/constants/backgroundColorsForNotes";
import DashedLines from "../../../../../../shared/svg/dashedLines";
import { getStatusLogConfig } from "../../../../../constants/statusLogConfig";
import { FollowUpTypeOption } from "../../../../../data-model/followUpTypeModel";
import ColorSelectorForOrder from "./colorSelectorForOrder";

export interface LogNotesProps {
  status: string;
  setComments: (comments: string) => void;
  setSelectedFollowUpType: (followUpType: {
    id: string;
    label: string;
  }) => void;
  setNotesColor: (color: string) => void;
  preSelectedNotesColor: string;
}

export const LogNotes = (props: LogNotesProps) => {
  const [selectedTemplates, setSelectedTemplates] = useState<number[]>([]);
  const [manualComment, setManualComment] = useState<string>("");
  const templates: string[] = useMemo(() => {
    return getStatusLogConfig(props.status).templates as unknown as string[];
  }, [props.status]);

  const followUpTypeOptions: FollowUpTypeOption[] = useMemo(() => {
    return getStatusLogConfig(props.status)
      .followUpTypes as unknown as FollowUpTypeOption[];
  }, [props.status]);

  const shouldShowTemplates = useMemo(() => {
    return templates && templates.length > 0;
  }, [templates]);

  const handleDeselectTemplate = (index: number) => {
    const newSelectedTemplates = selectedTemplates.filter((i) => i !== index);
    setSelectedTemplates(newSelectedTemplates);
    const remainingComments = newSelectedTemplates
      .map((i) => templates[i])
      .join(", ");
    props.setComments(remainingComments);
  };

  const handleSelectTemplate = (index: number) => {
    const newSelectedTemplates = [...selectedTemplates, index];
    setSelectedTemplates(newSelectedTemplates);
    const allComments = newSelectedTemplates
      .map((i) => templates[i])
      .join(", ");
    props.setComments(allComments);
    setManualComment("");
  };

  const handleFollowUpTypeChange = (selectedValue: { value: string }) => {
    const selectedOption = followUpTypeOptions.find(
      (option) => option.value === selectedValue.value,
    );
    if (selectedOption) {
      props.setSelectedFollowUpType({
        id: selectedOption.value,
        label: selectedOption.label,
      });
    }
  };

  return (
    <div className="status-log--log-event flex h-full flex-col">
      {/* Scrollable content area */}
      <div className="flex flex-1 flex-col gap-4 overflow-y-auto pb-4">
        <Select
          id="status-log-select"
          onOptionChange={handleFollowUpTypeChange}
          label="Follow Up Type"
          placeholder="Select the follow up type"
          options={followUpTypeOptions}
        />

        <div className="status-log--body--follow-up-type flex w-full flex-col gap-3">
          {shouldShowTemplates && (
            <div className="status-log--body--follow-up-type--label w-full text-h11 font-bold text-primaryGray-1">
              Select a Template
            </div>
          )}
          {shouldShowTemplates &&
            templates.map((template, index) => (
              <div
                key={index}
                className={`status-log--body--follow-up-type--templates flex w-full cursor-pointer flex-col gap-2 rounded rounded-s bg-primaryGray-16 p-3 transition-all duration-200 ${
                  selectedTemplates.includes(index)
                    ? "border-1 border-primaryGray-1 text-primaryGray-1"
                    : "border-1 border-transparent hover:border-gray-300"
                }`}
                onClick={() => {
                  if (selectedTemplates.includes(index)) {
                    handleDeselectTemplate(index);
                  } else {
                    handleSelectTemplate(index);
                  }
                }}
              >
                <div className="flex w-full text-wrap text-primaryGray-3">
                  {template}
                </div>
              </div>
            ))}
        </div>

        {selectedTemplates.length === 0 && shouldShowTemplates && (
          <div className="status-log--body--follow-up-type--templates flex w-full flex-row items-center gap-2">
            <DashedLines />
            <div className="text-h11 font-normal text-primaryGray-1">Or</div>
            <DashedLines />
          </div>
        )}

        {selectedTemplates.length === 0 && (
          <div className="comments-section w-full">
            <div className="comments-section header mb-3 text-h11 font-semibold text-primaryGray-2">
              Enter Manually
            </div>
            <div className="comments-section comments-textarea">
              <textarea
                className="h-42 w-full rounded border border-gray-300 p-2 text-tiny font-normal"
                placeholder="Enter your general comments here"
                value={manualComment}
                onChange={(e) => {
                  setManualComment(e.target.value);
                  props.setComments(e.target.value);
                  // Clear template selections when manual comment is entered
                  if (e.target.value.trim() && selectedTemplates.length > 0) {
                    setSelectedTemplates([]);
                  }
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Sticky color selector at bottom */}
      <div className="sticky bottom-0 border-t border-primaryGray-14 bg-white pt-4">
        <ColorSelectorForOrder
          backgroundColors={BACKGROUND_COLORS_FOR_NOTES}
          onColorSelect={props.setNotesColor}
          preSelectedNotesColor={props.preSelectedNotesColor ?? "transparent"}
        />
      </div>
    </div>
  );
};
